import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DMAutomation, NotificationType } from "@markit/common.types";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import {
  DMAutomationExistingData,
  fetchDMAutomationDuplicate,
} from "../../../../utils/dmAutomationUtils";
import CreateDMAutomationWrapper from "../Dashboard/CreateDMAutomationWrapper";

type CloneDMAutomationModalProps = {
  dmAutomation: DMAutomation;
  isModalVisible: boolean;
  setIsModalVisible: (isModalVisible: boolean) => void;
};

// Fetches the automation to clone as soon as it's shown, then opens the create
// flow prefilled with it (no intermediate confirmation screen).
const CloneDMAutomationModal = (props: CloneDMAutomationModalProps) => {
  const { dmAutomation, isModalVisible, setIsModalVisible } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountSlice);
  const [clonePrefill, setClonePrefill] = useState<
    DMAutomationExistingData | undefined
  >(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isModalVisible) {
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const prefill = await fetchDMAutomationDuplicate(
          accountData.uid,
          dmAutomation.id
        );
        setClonePrefill(prefill);
        setShowCreateModal(true);
      } catch (err) {
        console.error(err);
        showNotificationBanner(
          dispatch,
          "Failed to clone automation",
          NotificationType.NEGATIVE
        );
        setIsModalVisible(false);
      }
    })();
  }, [
    isModalVisible,
    accountData.uid,
    dmAutomation.id,
    dispatch,
    setIsModalVisible,
  ]);

  return showCreateModal && clonePrefill ? (
    <CreateDMAutomationWrapper
      showModal={showCreateModal}
      setShowModal={(show) => {
        setShowCreateModal(show);
        if (!show) {
          setClonePrefill(undefined);
          // The create flow is done (published or dismissed) - close the
          // whole clone flow.
          setIsModalVisible(false);
        }
      }}
      existingDmAutomation={clonePrefill.automation}
      existingDmTriggers={clonePrefill.triggers}
      existingDmContents={clonePrefill.contents}
      initialPlatform={clonePrefill.automation.platform}
    />
  ) : null;
};

export default CloneDMAutomationModal;
